// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Contract } from 'ethers'
import { DefiCollateralCTO, DefiType } from './CollateralSetCTO'
import { Erc20Wei } from './conversions'
import { DefiData } from './defiCTO'
import { getMainnetProviderForDefi, TimeStructure } from './defiVaultUtils'
import { ChainId, EXCHANGE_RATE_ONE } from './Enums'
import { logOT } from './loggingShared'
import { WILD_CAT_ABI } from './wildcatAbi'

export async function getWildcatNameAndSymbol(
  collateralCTO: DefiCollateralCTO
): Promise<{ name: string; symbol: string }> {
  const { defiVaultAddr } = collateralCTO
  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  const wintermuteContract = new Contract(defiVaultAddr, WILD_CAT_ABI, mainnetProvider)
  const symbol: string = await wintermuteContract.symbol()

  const name: string = await wintermuteContract.name()

  logOT.info('Wildcat Vault Info', {
    tag: 'Wildcat Vault Info',
    name,
    symbol,
    defiVaultAddr,
  })
  return { symbol, name }
}

async function getWildcatVaultInterestRate(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ interestRate: number }> {
  const { defiVaultAddr } = collateralCTO
  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  const wintermuteContract = new Contract(defiVaultAddr, WILD_CAT_ABI, mainnetProvider)
  let blockTag
  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure
    blockTag = Number(blockNumberEthereum)
  } else {
    blockTag = 'latest'
  }
  const annualInterestBips = await wintermuteContract.annualInterestBips({ blockTag })
  const interestRate = (Number(annualInterestBips) - 50) / 10000
  logOT.info('Wildcat Vault Info', {
    tag: 'Wildcat Vault Info',

    interestRate,
    defiVaultAddr,
  })
  return { interestRate }
}

async function getWildcatNativeDefiExchangeRateAndAPY(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{
  nativeDefiExchangeRateBeforeFees: bigint

  defiTotalShares: bigint
  defiTotalAssets: bigint
}> {
  const { defiVaultAddr } = collateralCTO
  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  const wintermuteContract = new Contract(defiVaultAddr, WILD_CAT_ABI, mainnetProvider)
  let defiTotalShares: bigint
  let defiTotalAssets: bigint
  let scaleFactor: bigint
  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure
    defiTotalShares = await wintermuteContract.scaledTotalSupply({ blockTag: Number(blockNumberEthereum) })
    defiTotalAssets = await wintermuteContract.totalSupply({ blockTag: Number(blockNumberEthereum) })
    scaleFactor = await wintermuteContract.scaleFactor({ blockTag: Number(blockNumberEthereum) })
  } else {
    defiTotalShares = await wintermuteContract.scaledTotalSupply()
    defiTotalAssets = await wintermuteContract.totalSupply()
    scaleFactor = await wintermuteContract.scaleFactor()
  }

  const nativeDefiExchangeRate = BigInt(scaleFactor)

  const nativeDefiExchangeRateBeforeFees: bigint =
    (nativeDefiExchangeRate * BigInt(collateralCTO.nativeDefiExchangeRateScale)) / EXCHANGE_RATE_ONE

  return { nativeDefiExchangeRateBeforeFees, defiTotalShares, defiTotalAssets }
}
async function getWildcatTransferWalletAssets(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ defiOTShares: bigint; defiOTAssets: bigint }> {
  const { defiVaultAddr, transferWalletAddr } = collateralCTO
  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  const wintermuteContract = new Contract(defiVaultAddr, WILD_CAT_ABI, mainnetProvider)
  let defiOTAssets: bigint
  let defiOTShares: bigint
  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure
    defiOTAssets = await wintermuteContract.balanceOf(transferWalletAddr, { blockTag: Number(blockNumberEthereum) })
    defiOTShares = await wintermuteContract.scaledBalanceOf(transferWalletAddr, {
      blockTag: Number(blockNumberEthereum),
    })
  } else {
    defiOTAssets = await wintermuteContract.balanceOf(transferWalletAddr)
    defiOTShares = await wintermuteContract.scaledBalanceOf(transferWalletAddr)
  }

  logOT.info({
    tag: 'Wildcat Calibration Assets',
    defiOTShares,
    defiOTAssets,
    defiVaultAddr,
  })
  return { defiOTShares, defiOTAssets }
}
async function getWildcatCalibrationAssets(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ calibrationAssets: bigint; calibratedExchangeRateBeforeFees: bigint }> {
  const { defiVaultAddr, calibrationWalletAddr } = collateralCTO

  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  const wintermuteContract = new Contract(defiVaultAddr, WILD_CAT_ABI, mainnetProvider)
  //const calibrationAssets: bigint = await wintermuteContract.balanceOf(calibrationWalletAddr)
  //const calibratedExchangeRate = (calibrationAssets * EXCHANGE_RATE_ONE) / 1000_000000n
  let calibrationAssets: bigint

  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure
    calibrationAssets = await wintermuteContract.balanceOf(calibrationWalletAddr, {
      blockTag: Number(blockNumberEthereum),
    })
  } else {
    calibrationAssets = await wintermuteContract.balanceOf(calibrationWalletAddr)
  }
  const calibratedExchangeRateBeforeFees = (calibrationAssets * EXCHANGE_RATE_ONE) / 1000_000000n
  return { calibrationAssets, calibratedExchangeRateBeforeFees }
}
export async function getDefiDataWildcat(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<DefiData> {
  const { interestRate } = await getWildcatVaultInterestRate(collateralCTO, timeStructure)
  const { nativeDefiExchangeRateBeforeFees, defiTotalShares, defiTotalAssets } =
    await getWildcatNativeDefiExchangeRateAndAPY(collateralCTO, timeStructure)
  const { defiOTShares, defiOTAssets } = await getWildcatTransferWalletAssets(collateralCTO, timeStructure)
  const { calibrationAssets, calibratedExchangeRateBeforeFees } = await getWildcatCalibrationAssets(
    collateralCTO,
    timeStructure
  )
  const wildcatRetValue: DefiData = {
    defiType: DefiType.Wildcat,
    interestRate,

    defiOTShares: Erc20Wei(defiOTShares),
    defiOTAssets: Erc20Wei(defiOTAssets),
    defiTotalShares: Erc20Wei(defiTotalShares),
    defiTotalAssets: Erc20Wei(defiTotalAssets),

    decimals: 6,
    calibratedBalance: Erc20Wei(calibrationAssets),
    calibratedExchangeRateBeforeFees: Erc20Wei(calibratedExchangeRateBeforeFees),
    nativeDefiExchangeRateBeforeFees: Erc20Wei(nativeDefiExchangeRateBeforeFees),

    collateralCTO: collateralCTO,
  }
  logOT.info('Wildcat GQL return', { tag: 'Wildcat GQL return', wildcatRetValue })
  return wildcatRetValue
}
